import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, AlertCircle } from 'lucide-react';
import { supabase } from '../../utils/supabase';

interface DonationLink {
  id: string;
  name: string;
  url: string;
  description: string;
  created_at?: string;
}

export function DonationLinksManager() {
  const [links, setLinks] = useState<DonationLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLink, setEditingLink] = useState<DonationLink | null>(null);
  const [formValues, setFormValues] = useState<Omit<DonationLink, 'id' | 'created_at'>>({
    name: '',
    url: '',
    description: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchDonationLinks();
  }, []);

  const fetchDonationLinks = async () => {
    try {
      setLoading(true);
      
      // Check if the donation_links table exists, if not, create it
      const { error: checkError } = await supabase.from('donation_links').select('id').limit(1);
      
      if (checkError && checkError.message.includes('does not exist')) {
        // Table doesn't exist, need to create it
        console.log('Donation links table does not exist, creating it...');
        // The actual creation will be done by the Supabase migration
      }
      
      const { data, error } = await supabase
        .from('donation_links')
        .select('*')
        .order('created_at', { ascending: true });

      if (error && !error.message.includes('does not exist')) {
        throw error;
      }

      setLinks(data || []);
    } catch (error) {
      console.error('Error fetching donation links:', error);
      setError('Failed to load donation links');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Validate form
      if (!formValues.name.trim()) {
        setError('Name is required');
        return;
      }

      if (!formValues.url.trim()) {
        setError('URL is required');
        return;
      }

      if (!formValues.url.startsWith('http://') && !formValues.url.startsWith('https://')) {
        setError('URL must start with http:// or https://');
        return;
      }

      if (editingLink) {
        // Update existing link
        const { error } = await supabase
          .from('donation_links')
          .update({
            name: formValues.name,
            url: formValues.url,
            description: formValues.description
          })
          .eq('id', editingLink.id);

        if (error) throw error;

        setSuccess('Donation link updated successfully');
      } else {
        // Add new link
        const { error } = await supabase
          .from('donation_links')
          .insert({
            name: formValues.name,
            url: formValues.url,
            description: formValues.description
          });

        if (error) throw error;

        setSuccess('Donation link added successfully');
      }

      // Reset form and refresh links
      resetForm();
      fetchDonationLinks();
    } catch (error) {
      console.error('Error saving donation link:', error);
      setError('Failed to save donation link');
    }
  };

  const handleEditLink = (link: DonationLink) => {
    setEditingLink(link);
    setFormValues({
      name: link.name,
      url: link.url,
      description: link.description
    });
  };

  const handleDeleteLink = async (id: string) => {
    if (!confirm('Are you sure you want to delete this donation link?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const { error } = await supabase
        .from('donation_links')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSuccess('Donation link deleted successfully');
      fetchDonationLinks();
    } catch (error) {
      console.error('Error deleting donation link:', error);
      setError('Failed to delete donation link');
    }
  };

  const resetForm = () => {
    setEditingLink(null);
    setFormValues({
      name: '',
      url: '',
      description: ''
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Manage Donation Links</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
          {success}
        </div>
      )}

      <form onSubmit={handleSaveLink} className="mb-8 p-4 bg-gray-50 rounded-lg">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="PayPal, iDEAL, etc."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL
            </label>
            <input
              type="url"
              name="url"
              value={formValues.url}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="https://..."
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <input
            type="text"
            name="description"
            value={formValues.description}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Support this project with a one-time donation"
          />
        </div>
        <div className="mt-4 flex justify-end space-x-3">
          {editingLink && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 flex items-center"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          >
            {editingLink ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update Link
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Link
              </>
            )}
          </button>
        </div>
      </form>

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Donation Links</h3>
        
        {links.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 rounded-lg text-gray-500">
            No donation links found. Add some above.
          </div>
        ) : (
          links.map((link) => (
            <div 
              key={link.id} 
              className="p-4 border border-gray-200 rounded-lg flex justify-between items-center"
            >
              <div>
                <h4 className="font-medium text-gray-900">{link.name}</h4>
                <p className="text-sm text-gray-500 break-words">{link.url}</p>
                {link.description && (
                  <p className="text-sm text-gray-600 mt-1">{link.description}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditLink(link)}
                  className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteLink(link.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}